import type { PmsDatabaseType } from '../types/pms';

/**
 * PRX-54 — the catalogue of UI blocks a pharmacy admin may hide, per role or
 * for everyone. This is the frontend half: keys + labels + descriptions +
 * groups + defaults, read by the Visibility tab and by every gate via
 * `resolveBlockHidden()` / `useBlockHidden()` (usePharmacyVisibility.ts).
 *
 * The backend keeps a matching key list (`backend-v2/src/pharmacies/visibility/
 * visibility-catalogue.ts`, the PATCH allow-list) and a drift spec
 * (`visibilityCatalogue.drift.spec.ts`) asserts the two key sets stay equal
 * (D6) — if you add, remove or rename a key here, update that file too.
 *
 * Source: docs/superpowers/specs/2026-09-11-visibility-preferences-design.md §2.
 */

export type VisibilityGroup =
  | 'Top bar'
  | 'Patient 360'
  | 'Order details'
  | 'Sidebar'
  | 'Billing'
  | 'Subscriptions'
  | 'Reports'
  | 'Queues';

export interface VisibilityContext {
  databaseType?: PmsDatabaseType | null;
}

export interface VisibilityBlock {
  key: string;
  group: VisibilityGroup;
  label: string;
  description: string;
  /** The block's state when the pharmacy has never set it. A function when it depends on context. */
  defaultHidden: boolean | ((ctx: VisibilityContext) => boolean);
  /**
   * PRX-195: a pharmacy-wide setting, never per role. The block changes the
   * clinical workflow, not just what is on screen, so its gate resolves with
   * no role and the Visibility tab offers no "Hidden for these roles" picker.
   */
  pharmacyWide?: boolean;
}

/**
 * PRX-195 / PRX-203: the "Which pathway is this?" Specialty/Digital question.
 * Hidden means the pharmacy has no Specialty pathway (PharmAssist): every new
 * start, add or switch is Digital. Pharmacy-wide; read through
 * `usePharmacyHasNoSpecialtyPathway()`.
 */
export const PATHWAY_CHOICE_VISIBILITY_KEY = 'subscription.step.pathway_choice';

/**
 * PRX-211 Q9 (from the PRX-202 recording): the new start wizard's "Delivery
 * Type" step, Patient home delivery or Infusion clinic. Hidden means the
 * pharmacy has no infusion clinic (PharmAssist): the step is not shown and
 * the subscription is Patient home delivery. Pharmacy-wide; read through
 * `usePharmacyHidesDeliveryTypeChoice()`.
 */
export const DELIVERY_TYPE_CHOICE_VISIBILITY_KEY = 'subscription.step.delivery_type';

/** PRX-207: the "Check all" control on the counselling documentation. */
export const COUNSELING_CHECK_ALL_VISIBILITY_KEY = 'counseling.action.check_all';

/** PRX-209: the outstanding payment chip in the order details header. */
export const ORDER_OUTSTANDING_PAYMENT_VISIBILITY_KEY = 'orderdetail.chip.outstanding_payment';

export const VISIBILITY_CATALOGUE: readonly VisibilityBlock[] = [
  {
    key: 'topbar.action.operations_dashboard',
    group: 'Top bar',
    label: 'Operations dashboard button',
    description: 'Quick action in the top bar; the page stays in the sidebar.',
    defaultHidden: true,
  },
  {
    key: 'topbar.action.search',
    group: 'Top bar',
    label: 'Search Rx, patient or order button',
    description: 'Quick action in the top bar; the search box and Shift+X still open the search.',
    defaultHidden: true,
  },
  {
    key: 'topbar.action.new_order',
    group: 'Top bar',
    label: 'New order button',
    description: 'Quick action in the top bar.',
    defaultHidden: false,
  },
  {
    key: 'topbar.action.all_orders',
    group: 'Top bar',
    label: 'All orders button',
    description: 'Quick action in the top bar; the page stays in the sidebar.',
    defaultHidden: true,
  },
  {
    key: 'topbar.action.task_queue',
    group: 'Top bar',
    label: 'Task queue button',
    description: 'Quick action in the top bar; Shift+T still opens the queue.',
    defaultHidden: true,
  },
  {
    // PRX-196: PharmAssist is one pharmacy, not part of the GSP network, so
    // the cross-pharmacy switch means nothing there. Hiding it also stops
    // identifier queries (ORD-, Rx and phone numbers) broadening to every
    // pharmacy on their own. Shown everywhere else, and while the PMS is unknown.
    key: 'topbar.search.all_pharmacies',
    group: 'Top bar',
    label: 'Search all GSP pharmacies switch',
    description:
      'In the search palette. When hidden, search stays on this pharmacy: the switch is gone and order, Rx and phone numbers no longer search every pharmacy on their own. Hidden by default on a Fillware pharmacy.',
    defaultHidden: (ctx) => ctx.databaseType === 'fillware',
  },
  {
    key: 'patient360.tab.notes_documents',
    group: 'Patient 360',
    label: 'Notes & documents tab',
    description: 'Patient 360 tab.',
    defaultHidden: false,
  },
  {
    key: 'patient360.tab.insurance_psp',
    group: 'Patient 360',
    label: 'Insurance & PSP tab',
    description: 'Patient 360 tab.',
    defaultHidden: false,
  },
  {
    key: 'patient360.tab.new_start_info',
    group: 'Patient 360',
    label: 'New start info tab',
    description: 'Patient 360 tab.',
    defaultHidden: false,
  },
  {
    key: 'patient360.tab.audit_log',
    group: 'Patient 360',
    label: 'Audit log tab',
    description: 'Patient 360 tab.',
    defaultHidden: false,
  },
  {
    // PRX-131: the patient's invoices and balance (ADR-111). Shown by default on
    // a Fillware pharmacy only, like the PRX-143 PHOX Entry sidebar item.
    key: 'patient360.tab.payments',
    group: 'Patient 360',
    label: 'Payments tab',
    description:
      "Patient 360 tab: the patient's invoices, balance owing and payments. Shown by default on a Fillware pharmacy only.",
    defaultHidden: (ctx) => ctx.databaseType !== 'fillware',
  },
  {
    key: 'patient360.tab.timeline',
    group: 'Patient 360',
    label: 'Timeline tab',
    description: 'Patient 360 tab: every touchpoint with the patient, newest first (PRX-35).',
    defaultHidden: false,
  },
  {
    key: 'patient360.subtab.prescription_images',
    group: 'Patient 360',
    label: 'Prescription images sub-tab',
    description: 'Under Prescriptions & Drugs; the Hardcopy column opens the same images.',
    defaultHidden: true,
  },
  {
    key: 'patient360.column.cf',
    group: 'Patient 360',
    label: 'CF (central fill) column',
    description:
      'Prescriptions table. Central fill is a Kroll concept; hidden by default on a Fillware pharmacy.',
    defaultHidden: (ctx) => ctx.databaseType === 'fillware',
  },
  {
    key: 'patient360.column.rx_stage',
    group: 'Patient 360',
    label: 'Order stage column',
    description: 'Prescriptions table. The workflow stage of the most recent PHOX order that carries the Rx, with a count when several do.',
    defaultHidden: false,
  },
  {
    key: 'patient360.column.rx_specialty',
    group: 'Patient 360',
    label: 'Specialty marker column',
    description: 'Prescriptions table. The selection checkbox and the Mark as specialty toolbar.',
    defaultHidden: false,
  },
  {
    key: 'patient360.column.rx_dispense_status',
    group: 'Patient 360',
    label: 'Dispense status column',
    description:
      'Prescriptions table. The Kroll dispense status, or on a Fillware pharmacy the fill status: Billed, Not billed, Cash, not claimed, Claim rejected, Claim reversed, On hold, Transfer in pending, Discontinued, Cancelled or Transferred out.',
    // PRX-165 B1: was hidden by default on Fillware while it only held the
    // Kroll 1-8 map; it now carries the Fillware fill status, so it is shown.
    defaultHidden: false,
  },
  {
    key: 'patient360.card.patient_flags',
    group: 'Patient 360',
    label: 'Patient flags card',
    description: 'Patient info tab, Contact & Address. VIP, refill type, polypharmacy, file status.',
    defaultHidden: false,
  },
  {
    key: 'patient360.card.system_ids',
    group: 'Patient 360',
    label: 'System ids card',
    description: 'Patient info tab, Contact & Address. The PMS patient id and MRN.',
    defaultHidden: false,
  },
  {
    key: 'patient360.card.relatives_caregivers',
    group: 'Patient 360',
    label: 'Relatives & caregivers card',
    description: 'Patient info tab, Contact & Address.',
    defaultHidden: false,
  },
  {
    key: 'patient360.card.clinical_services',
    group: 'Patient 360',
    label: 'Clinical services card',
    description: 'Patient info tab, Contact & Address.',
    defaultHidden: false,
  },
  {
    key: 'patient360.card.prescriber_clinic',
    group: 'Patient 360',
    label: 'Prescriber & clinic card',
    description: 'Patient info tab, Contact & Address.',
    defaultHidden: false,
  },
  {
    key: 'patient360.card.refill_notes',
    group: 'Patient 360',
    label: 'Refill notes card',
    description: 'Patient info tab, Contact & Address.',
    defaultHidden: false,
  },
  {
    key: 'patient360.card.permanent_note',
    group: 'Patient 360',
    label: 'Permanent note card',
    description: 'Patient info tab, Contact & Address.',
    defaultHidden: false,
  },
  {
    key: 'patient360.card.allergies_vitals',
    group: 'Patient 360',
    label: 'Allergies & vitals card',
    description: 'Patient info tab, Contact & Address. Reuses the shared allergies/medical-profile summary.',
    defaultHidden: false,
  },
  {
    key: 'patient360.card.medical_conditions',
    group: 'Patient 360',
    label: 'Medical conditions card',
    description: 'Patient info tab, Contact & Address. Reuses the shared allergies/medical-profile summary.',
    defaultHidden: false,
  },
  {
    key: 'patient360.card.blister_schedule',
    group: 'Patient 360',
    label: 'Pack schedule card',
    description:
      'Blister pack tab, above the per-drug grid. Cycle, pack size, pack dates and the blister auth due date.',
    defaultHidden: false,
  },
  {
    key: 'patient360.card.blister_notes',
    group: 'Patient 360',
    label: 'Blister notes card',
    description: 'Blister pack tab. Free-text notes about the pack.',
    defaultHidden: false,
  },
  {
    key: 'patient360.card.blister_goes_with_pack',
    group: 'Patient 360',
    label: 'Goes with the pack card',
    description:
      'Blister pack tab. The drugs that ship with the pack but are not blistered, with how often each goes.',
    defaultHidden: false,
  },
  {
    key: 'patient360.card.blister_manual_billing',
    group: 'Patient 360',
    label: 'Manual billing card',
    description:
      'Blister pack tab. The prescriptions billed by hand rather than from the pack cycle, with who added each and when.',
    defaultHidden: false,
  },
  {
    key: 'patient360.card.specialty_drugs',
    group: 'Patient 360',
    label: 'Specialty drugs card',
    description:
      'Insurance & PSP tab. The read-only list of drugs marked as specialty, with stage, dates and prescriber.',
    defaultHidden: false,
  },
  {
    // PRX-144: Rafik's summary strip and action panel. Its rules read
    // PharmAssist's payments ledger, household and blister workflow, so a
    // Kroll/GreenShield store does not see it unless an admin shows it, and it
    // stays hidden while the pharmacy's PMS is not yet known.
    key: 'patient360.card.action_panel',
    group: 'Patient 360',
    label: 'Summary strip and action panel',
    description:
      'Under the Patient 360 header: due date, current order, delivery date, stage and warnings, with the "n Action" panel listing what needs attention. Shown by default on a Fillware pharmacy only.',
    defaultHidden: (ctx) => ctx.databaseType !== 'fillware',
  },
  {
    // PRX-209: Rafik always wants to see, without opening anything, whether
    // the prescription still has money owing. Read from the order's own
    // payment collection status and outstanding balance. Shown by default on a
    // Fillware pharmacy only, like the PRX-131 Payments tab.
    key: ORDER_OUTSTANDING_PAYMENT_VISIBILITY_KEY,
    group: 'Order details',
    label: 'Outstanding payment chip',
    description:
      'Order details header, beside the status. Says when the order still has a payment owing, with the balance, and when it was collected or waived. Shown by default on a Fillware pharmacy only.',
    defaultHidden: (ctx) => ctx.databaseType !== 'fillware',
  },
  {
    key: 'orderdetail.card.counseling_history',
    group: 'Order details',
    label: 'Counseling history card',
    description: 'Order details page.',
    defaultHidden: false,
  },
  {
    key: 'orderdetail.card.rx_images',
    group: 'Order details',
    label: 'Rx images card',
    description: 'Order details page.',
    defaultHidden: false,
  },
  {
    key: 'orderdetail.card.billing_information',
    group: 'Order details',
    label: 'Billing information card',
    description: 'Order details page.',
    defaultHidden: false,
  },
  {
    key: 'orderdetail.card.sms_activity_log',
    group: 'Order details',
    label: 'SMS activity log card',
    description: 'Order details page.',
    defaultHidden: false,
  },
  {
    key: 'orderdetail.card.action_log',
    group: 'Order details',
    label: 'Action log card',
    description: 'Order details page.',
    defaultHidden: false,
  },
  {
    // PRX-126: a pharmacy that ships through couriers has no drivers of its
    // own. Shown by default on every PMS, so a pharmacy with drivers sees no
    // change until an admin hides it.
    key: 'sidebar.section.driver_management',
    group: 'Sidebar',
    label: 'Driver management section',
    description:
      'Sidebar section with Driver Management, the bag scan and driver select kiosks, bag history and checkout history. Hide it for a pharmacy with no drivers of its own; the pages stay reachable by their address.',
    defaultHidden: false,
  },
  {
    // PRX-104: the pharmacy-wide Blister pack page. Blister packs are a
    // Fillware (PharmAssist) workflow, so a Kroll/GreenShield store does not see
    // the entry unless an admin shows it. While the pharmacy's PMS is not yet
    // known the entry stays hidden rather than flashing in.
    key: 'sidebar.item.blister_pack',
    group: 'Sidebar',
    label: 'Blister pack page',
    description:
      'Sidebar entry for the pharmacy-wide Blister pack page (billing due and the SynMed list). Shown by default on a Fillware pharmacy only; the page stays reachable by its address.',
    defaultHidden: (ctx) => ctx.databaseType !== 'fillware',
  },
  {
    // PRX-134: the Doctors directory and Prescriber clinics. The directory
    // reads Fillware prescribers, so a Kroll/GreenShield store does not see the
    // entry unless an admin shows it, and it stays hidden while the PMS is unknown.
    key: 'sidebar.item.prescribers',
    group: 'Sidebar',
    label: 'Prescribers section',
    description:
      'Sidebar entry for Prescribers: the Doctors directory and Prescriber clinics. Shown by default on a Fillware pharmacy only; the pages stay reachable by their address.',
    defaultHidden: (ctx) => ctx.databaseType !== 'fillware',
  },
  {
    // PRX-143: the PHOX Entry intake queue (requests pushed from the
    // Communication Center). Rafik's PharmAssist workflow, so a Kroll/GreenShield
    // store does not see the entry unless an admin shows it, and it stays hidden
    // while the pharmacy's PMS is not yet known.
    key: 'sidebar.item.phox_entry_queue',
    group: 'Sidebar',
    label: 'PHOX Entry queue',
    description:
      'Sidebar entry under Main Operations for the PHOX Entry queue: texts pushed from the Communication Center, waiting to be entered as orders. Shown by default on a Fillware pharmacy only; the page stays reachable by its address.',
    defaultHidden: (ctx) => ctx.databaseType !== 'fillware',
  },
  {
    // PRX-129: the PSP queue (specialty drug files and prior authorization).
    // Rafik's PharmAssist workflow, so a Kroll/GreenShield store does not see
    // the entry unless an admin shows it, and it stays hidden while the
    // pharmacy's PMS is not yet known.
    key: 'sidebar.item.psp_queue',
    group: 'Sidebar',
    label: 'PSP queue',
    description:
      'Sidebar entry for the PSP queue: specialty drug files from Request received through coverage to the prescriber. Shown by default on a Fillware pharmacy only; the page stays reachable by its address.',
    defaultHidden: (ctx) => ctx.databaseType !== 'fillware',
  },
  {
    // PRX-139: the Calendar (appointments and MedsCheck booking), from Rafik's
    // PharmAssist wireframe. A Kroll/GreenShield store does not see the entry
    // unless an admin shows it, and it stays hidden while the pharmacy's PMS
    // is not yet known.
    key: 'sidebar.item.calendar',
    group: 'Sidebar',
    label: 'Calendar',
    description:
      'Sidebar entry for the Calendar: appointments by month, week and day, staff calendars and Coming up. Shown by default on a Fillware pharmacy only; the page stays reachable by its address.',
    defaultHidden: (ctx) => ctx.databaseType !== 'fillware',
  },
  {
    // PRX-140: the time clock and payroll (Rafik's back office). PharmAssist
    // asked for it, so a Kroll/GreenShield store does not see the entry unless
    // an admin shows it, and it stays hidden while the PMS is not yet known.
    key: 'sidebar.item.time_clock',
    group: 'Sidebar',
    label: 'Time clock',
    description:
      'Sidebar entry for the Time clock: clock in and out, breaks, missing-punch reports and, for managers, live status, review, payroll and rates. Shown by default on a Fillware pharmacy only; the page stays reachable by its address.',
    defaultHidden: (ctx) => ctx.databaseType !== 'fillware',
  },
  {
    // PRX-122: discount card warning on Process Billing. Rafik's card list and
    // the PharmAssist billing workflow are Fillware's, so a Kroll/GreenShield
    // store does not see it unless an admin shows it, and it stays hidden while
    // the pharmacy's PMS is not yet known.
    key: 'billing.card.discount_cards',
    group: 'Billing',
    label: 'Discount cards notice',
    description:
      'Process Billing, under the prescriptions. For each selected Rx with a manufacturer discount or co-pay card: the card to use and where to enrol. Warns only. Shown by default on a Fillware pharmacy only.',
    defaultHidden: (ctx) => ctx.databaseType !== 'fillware',
  },
  {
    // PRX-195 / PRX-203: PharmAssist has no Specialty pathway. Hiding the
    // "Which pathway is this?" question makes every new start, Add Drug and
    // Medication Switch Digital (no New Start form). Hidden by default on a
    // Fillware pharmacy only; Kroll/GreenShield and an unknown PMS keep asking.
    key: PATHWAY_CHOICE_VISIBILITY_KEY,
    group: 'Subscriptions',
    label: 'Specialty or Digital pathway question',
    description:
      'The "Which pathway is this?" question on a new start, Add Drug and Medication Switch / Dose Change. When hidden, the pathway is Digital and there is no New Start form. Applies to the whole pharmacy, not per role. Hidden by default on a Fillware pharmacy only.',
    defaultHidden: (ctx) => ctx.databaseType === 'fillware',
    pharmacyWide: true,
  },
  {
    // PRX-211 Q9: PharmAssist has no infusion clinic. Hiding the Delivery Type
    // step makes every new start Patient home delivery, with no choice shown.
    // Hidden by default on a Fillware pharmacy only; Kroll/GreenShield and an
    // unknown PMS keep the step.
    key: DELIVERY_TYPE_CHOICE_VISIBILITY_KEY,
    group: 'Subscriptions',
    label: 'Delivery type step (home delivery or infusion clinic)',
    description:
      'The Delivery Type step of a new start: Patient home delivery or Infusion clinic. When hidden, the step is skipped and the subscription is Patient home delivery. Applies to the whole pharmacy, not per role. Hidden by default on a Fillware pharmacy only.',
    defaultHidden: (ctx) => ctx.databaseType === 'fillware',
    pharmacyWide: true,
  },
  {
    // PRX-136: Rafik's patient cohort reports (blister, VIP, specialty drug,
    // past due, professions). They read PharmAssist's Zoho-sourced profile
    // fields and the blister workflow, so a Kroll/GreenShield store does not
    // see the card unless an admin shows it, and it stays hidden while the
    // pharmacy's PMS is not yet known.
    key: 'reports.patient_cohorts',
    group: 'Reports',
    label: 'Patient reports',
    description:
      'Reports page card for the patient lists: blister pack, VIP, specialty drug at another pharmacy, monthly and blister past due, and professions. Shown by default on a Fillware pharmacy only; the page stays reachable by its address.',
    defaultHidden: (ctx) => ctx.databaseType !== 'fillware',
  },
  {
    // PRX-192/193/194: PharmAssist has no nursing homes. Hidden on a Fillware
    // pharmacy only; a Kroll/GreenShield store, and one whose PMS is not yet
    // known, keeps it. While hidden, a filter saved in the browser is ignored
    // (useGatedNursingHomeFilter), so it cannot narrow the queue unseen.
    key: 'queues.filter.nursing_home',
    group: 'Queues',
    label: 'Nursing home filter',
    description:
      'The Nursing Home filter on the Clinical verification, Packaging, Product verification, Scheduling, Staging, Ready to ship and Subscription management queues. While hidden, a saved nursing home filter is ignored. Hidden by default on a Fillware pharmacy.',
    defaultHidden: (ctx) => ctx.databaseType === 'fillware',
  },
  {
    // PRX-207: Rafik asked for one control that ticks every counselling point
    // (PRX-211 Q6). Staff can still untick or tick points one at a time.
    // Shown by default on a Fillware pharmacy only, so GreenShield's
    // counselling documentation is unchanged unless an admin shows it.
    key: COUNSELING_CHECK_ALL_VISIBILITY_KEY,
    group: 'Queues',
    label: 'Check all on counselling documentation',
    description:
      'In the counselling window opened from Clinical verification, the Task queue and Patient 360: one Check all control that ticks every counselling point at once. Points can still be unticked one by one. Shown by default on a Fillware pharmacy only.',
    defaultHidden: (ctx) => ctx.databaseType !== 'fillware',
  },
  {
    // PRX-193: mixtures are a GreenShield packaging concern.
    key: 'queues.packaging.filter.mixtures',
    group: 'Queues',
    label: 'Mixtures filter',
    description:
      'The Mixtures checkbox in the Packaging queue filters. While hidden, the queue is not narrowed to mixtures. Hidden by default on a Fillware pharmacy.',
    defaultHidden: (ctx) => ctx.databaseType === 'fillware',
  },
  {
    // PRX-194: four checkboxes crowd the Scheduling header. Hiding them puts
    // the same four filters in one compact Blister filters dropdown; the
    // filtering is identical either way.
    key: 'queues.scheduling.blister_checkboxes',
    group: 'Queues',
    label: 'Blister checkboxes on Scheduling',
    description:
      'The four blister checkboxes in the Scheduling queue header. When hidden, the same four filters sit in one Blister filters dropdown and filter the same way. Hidden by default on a Fillware pharmacy.',
    defaultHidden: (ctx) => ctx.databaseType === 'fillware',
  },
];

export const VISIBILITY_KEYS = VISIBILITY_CATALOGUE.map((b) => b.key);
